import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Database,
  ArrowRightLeft,
  Shield,
  BookOpen,
  Plug,
  Server,
  Workflow,
  AlertTriangle,
  Users,
  Building2,
  Settings,
  Inbox,
  ListChecks,
  BarChart3,
  Globe,
  ScrollText,
  FileSearch,
  Upload,
  RefreshCcw,
  Radio,
  Scale,
  Bell,
  Weight,
  Warehouse,
  Gauge,
  PlugZap,
  Table2,
  Bot,
  Compass,
  FileText,
  FileCheck2,
  NotebookPen,
  HelpCircle,
  Menu,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/auth/useAuth';
import { getSetupCompleteness } from '@/api/setup-api';
import { ROUTES } from '@/lib/routes';
import type { MenuItem } from '@/api/types';

// Map icon names from API to Lucide components
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Database,
  ArrowRightLeft,
  Shield,
  BookOpen,
  Plug,
  Server,
  Workflow,
  AlertTriangle,
  Users,
  Building2,
  Settings,
  Inbox,
  ListChecks,
  BarChart3,
  Globe,
  ScrollText,
  FileSearch,
  Upload,
  RefreshCcw,
  Radio,
  Scale,
  Bell,
  Weight,
  Warehouse,
  Gauge,
  PlugZap,
  Table2,
  Bot,
  Compass,
  FileText,
  FileCheck2,
  NotebookPen,
};

// ── Sidebar section grouping ────────────────────────────────────
// Groups menu items by concern: what the user monitors, configures, or administers.
// Items are ordered within each section by the menuIds array order.
// Any menuId not listed here falls into an implicit "Other" group at the bottom.

interface SectionConfig {
  label: string;
  /** Plain-language label shown in the functional theme. */
  title: string;
  menuIds: string[];
  /** Rendered as its own product module block, not a plain group. */
  module?: { title: string; tagline: string; icon: React.ComponentType<{ className?: string }> };
}

const SIDEBAR_SECTIONS: SectionConfig[] = [
  {
    label: '// Operations',
    title: 'Operación',
    menuIds: ['dashboard', 'quota', 'polling-status', 'dlq', 'integration-audit', 'reconciliation', 'alerts', 'audit-trail', 'demo-inbox'],
  },
  {
    label: '// Data_Hub',
    title: 'Data Hub',
    menuIds: ['data-hub', 'data-hub-dashboards', 'data-hub-explore', 'data-hub-reports', 'data-hub-agents', 'data-hub-powerbi', 'data-hub-tables'],
    module: { title: 'Data Hub', tagline: 'Datos de Epicor al día', icon: Warehouse },
  },
  {
    label: '// Documents',
    title: 'Documentos',
    menuIds: ['documents', 'documents-instructions'],
    module: { title: 'Documentos', tagline: 'Facturas y remisiones a Epicor', icon: FileText },
  },
  {
    label: '// Integration_Config',
    title: 'Integración',
    menuIds: ['systems', 'connections', 'entity-registry', 'mappings', 'schemas', 'lookup-tables', 'uom-conversions', 'workflows'],
  },
  {
    label: '// Administration',
    title: 'Administración',
    menuIds: ['users', 'companies', 'settings', 'config-sync', 'initial-load'],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { menus, user, roleName } = useAuth();

  // Check setup completeness — only runs when menus are loaded (user is authenticated)
  const { data: setupData } = useQuery({
    queryKey: ['setup-completeness'],
    queryFn: getSetupCompleteness,
    enabled: menus.length > 0,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const showSetup = setupData ? !setupData.setupComplete : false;

  // Group menu items by section, preserving role-based filtering from the API.
  // Injects a synthetic "Setup" item at the top of Integration Config when setup is incomplete.
  const groupedMenus = useMemo(() => {
    const menuMap = new Map(menus.map((m) => [m.menuId, m]));

    const sections = SIDEBAR_SECTIONS.map((section) => {
      const items: MenuItem[] = section.menuIds
        .map((id) => menuMap.get(id))
        .filter((item): item is MenuItem => item !== undefined);

      // Inject Setup item at top of Integration Config when setup is incomplete
      if (section.label === '// Integration_Config' && showSetup) {
        items.unshift({
          menuId: 'setup',
          name: 'Setup',
          path: ROUTES.SETUP,
          icon: 'ListChecks',
          order: 0,
        });
      }

      return { ...section, items };
    }).filter((section) => section.items.length > 0);

    return sections;
  }, [menus, showSetup]);

  // Get user initials for avatar
  const getUserInitials = () => {
    const email = user?.signInDetails?.loginId ?? user?.username ?? '';
    if (!email) return 'U';
    const localPart = email.split('@')[0] ?? email;
    const parts = localPart.split(/[._-]/);
    const first = parts[0];
    const second = parts[1];
    if (parts.length >= 2 && first && second && first.length > 0 && second.length > 0) {
      return (first.charAt(0) + second.charAt(0)).toUpperCase();
    }
    return localPart.slice(0, 2).toUpperCase();
  };

  const userName = user?.signInDetails?.loginId?.split('@')[0] ?? user?.username ?? 'User';

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-scrim/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar: dark in both themes, so the raw palette keeps its dark values */}
      <aside
        className={cn(
          'palette-dark fixed inset-y-0 left-0 z-50 flex w-[220px] flex-col border-r border-sidebar-border bg-sidebar transition-transform duration-300 ease-in-out lg:static lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo Header */}
        <div className="flex h-14 items-center gap-2 border-b border-sidebar-border px-4">
          <img src="/logo-icon.png" alt="Integration Studio" className="h-7 w-auto" />
          <span className="ui-caps text-sm font-semibold uppercase tracking-wider text-white/90">Int. Studio</span>
          <button
            onClick={onClose}
            className="ml-auto rounded p-1 text-gray-500 hover:bg-white/5 hover:text-white lg:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* System Status Section */}
        <div className="terminal-only border-b border-sidebar-border px-4 py-3">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-sidebar-accent">// System_Status</p>
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">UPTIME:</span>
              <span className="font-medium text-sidebar-accent">99.97%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">LATENCY:</span>
              <span className="font-medium text-white">12ms</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">VERSION:</span>
              <span className="font-medium text-white">v3.2.1</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 scrollbar-thin">
          {menus.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-700 border-t-sidebar-accent" />
              <span className="text-xs text-gray-500">Loading...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedMenus.map((section) => (
                <div
                  key={section.label}
                  className={cn(
                    section.module &&
                      'rounded-lg border border-sidebar-accent/20 bg-gradient-to-b from-sidebar-accent/[0.07] to-transparent p-1.5',
                  )}
                >
                  {section.module ? (
                    <div className="mb-1.5 flex items-center gap-2 px-2 pt-1">
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-sidebar-accent/15 text-sidebar-accent">
                        <section.module.icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="ui-caps block text-[11px] font-semibold uppercase tracking-wider text-white">{section.module.title}</span>
                        <span className="block truncate text-[10px] text-gray-500">{section.module.tagline}</span>
                      </span>
                      <span className="ui-caps rounded border border-sidebar-accent/30 px-1 text-[9px] font-medium uppercase tracking-wider text-sidebar-accent">
                        Módulo
                      </span>
                    </div>
                  ) : (
                    <>
                      <p className="terminal-only mb-1.5 px-3 text-[10px] font-medium uppercase tracking-wider text-sidebar-accent">
                        {section.label}
                      </p>
                      <p className="functional-only mb-1.5 px-3 text-[11px] font-semibold text-sidebar-foreground/70">
                        {section.title}
                      </p>
                    </>
                  )}
                  <div className="space-y-0.5">
                    {section.items.map((item) => {
                      const IconComponent = ICON_MAP[item.icon] ?? HelpCircle;
                      return (
                        <NavLink
                          key={item.menuId}
                          to={item.path}
                          end={item.path === '/' || item.path === '/data-hub' || item.path === '/documents'}
                          onClick={() => {
                            if (window.innerWidth < 1024) {
                              onClose();
                            }
                          }}
                          className={({ isActive }: { isActive: boolean }) =>
                            cn(
                              'flex items-center gap-2.5 rounded px-3 py-2 text-xs font-medium transition-all duration-150',
                              isActive
                                ? 'bg-sidebar-accent/10 text-sidebar-accent border-l-2 border-sidebar-accent'
                                : 'text-sidebar-foreground hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                            )
                          }
                        >
                          <IconComponent className="h-4 w-4 shrink-0" />
                          <span className="ui-caps flex-1 uppercase tracking-wide">{item.name}</span>
                          {item.menuId === 'setup' && setupData && (
                            <span className="ml-auto rounded bg-sidebar-accent/20 px-1.5 py-0.5 text-[10px] font-medium text-sidebar-accent">
                              {setupData.completedCount}/{setupData.totalSteps}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </nav>

        {/* User Profile Section */}
        <div className="border-t border-sidebar-border p-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded bg-sidebar-accent/80 text-[10px] font-bold text-white">
              {getUserInitials()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="ui-caps truncate text-xs font-medium uppercase tracking-wide text-white">{userName}</p>
              <p className="ui-caps text-[10px] uppercase tracking-wide text-sidebar-foreground/70">{roleName || 'User'}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

// Mobile menu button component
export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded p-2 text-subtle hover:bg-foreground/5 hover:text-foreground lg:hidden"
      aria-label="Open sidebar"
    >
      <Menu className="h-5 w-5" />
    </button>
  );
}
